import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Save, User, Car, Package, Clock, Globe, Calendar, BarChart3, Image, Type, Eye, X, CheckCircle2, Upload } from 'lucide-react';
import Modal from '../../components/Modal';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

export default function EventConfigPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user, role } = useOutletContext() || {};
    const isClient = role === 'client';
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    
    // Data for dropdowns
    const [oems, setOems] = useState([]);
    const [groups, setGroups] = useState([]);
    const [existingDomains, setExistingDomains] = useState([]);
    const [dealerLocations, setDealerLocations] = useState([]);
    const [users, setUsers] = useState([]);
    const [brands, setBrands] = useState([]);
    
    // Form state
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        event_type: 'appointment',
        oem_id: '',
        group_id: '',
        dealer_location_ids: [],
        appointment_duration_minutes: 30,
        gap_duration_minutes: 15,
        is_active: true,
        is_public: true,
        start_date: '',
        end_date: '',
        custom_hostname: '',
        banner_text: '',
        banner_image_url: '',
        banner_background_color: '#ffffff',
        banner_text_color: '#000000',
        banner_font_size: 24,
        banner_is_active: false,
        address: '',
        contact_email: '',
        contact_phone: ''
    });
    
    // Banner editor state
    const [showBannerEditor, setShowBannerEditor] = useState(false);
    const [bannerUploading, setBannerUploading] = useState(false);
    
    // Sales team, vehicles, accessories
    const [salesTeam, setSalesTeam] = useState([]);
    const [vehicleModels, setVehicleModels] = useState([]);
    const [accessories, setAccessories] = useState([]);
    
    // Filter dealer locations by selected group
    // Only show locations if a group is selected AND that group has locations
    const filteredDealerLocations = formData.group_id
        ? dealerLocations.filter(loc => loc.group_id === formData.group_id)
        : [];
    
    // Modals
    const [showSalesTeamModal, setShowSalesTeamModal] = useState(false);
    const [showVehicleModal, setShowVehicleModal] = useState(false);
    const [showAccessoryModal, setShowAccessoryModal] = useState(false);
    const [editingSalesTeam, setEditingSalesTeam] = useState(null);
    const [editingVehicle, setEditingVehicle] = useState(null);
    const [editingAccessory, setEditingAccessory] = useState(null);

    useEffect(() => {
        if (id) {
            fetchEvent();
        }
        fetchDropdownData();
    }, [id]);


    const fetchDropdownData = async () => {
        const token = localStorage.getItem('accessToken');
        const headers = { 'Authorization': `Bearer ${token}` };

        // Fetch OEMs
        try {
            const res = await fetch(`${API_URL}/oems`, { headers });
            if (res.ok) {
                const data = await res.json();
                setOems(data.filter(o => o.is_active !== false));
            }
        } catch (err) {
            console.error('Error fetching OEMs:', err);
        }

        // Fetch Groups
        try {
            const res = await fetch(`${API_URL}/groups`, { headers });
            if (res.ok) {
                const data = await res.json();
                setGroups(data);
            }
        } catch (err) {
            console.error('Error fetching groups:', err);
        }

        // Fetch Dealer Locations
        try {
            const res = await fetch(`${API_URL}/dealer-locations-with-addresses`, { headers });
            if (res.ok) {
                const data = await res.json();
                setDealerLocations(data);
            }
        } catch (err) {
            console.error('Error fetching dealer locations:', err);
        }

        // Fetch Users
        try {
            const res = await fetch(`${API_URL}/users`, { headers });
            if (res.ok) {
                const data = await res.json();
                setUsers(data.users || data || []);
            }
        } catch (err) {
            console.error('Error fetching users:', err);
        }

        // Fetch Brands
        try {
            const res = await fetch(`${API_URL}/brands`, { headers });
            if (res.ok) {
                const data = await res.json();
                setBrands(data);
            }
        } catch (err) {
            console.error('Error fetching brands:', err);
        }

        // Fetch existing domains (for portal domain selection)
        try {
            const res = await fetch(`${API_URL}/events`, { headers });
            if (res.ok) {
                const data = await res.json();
                const domains = [...new Set((data || []).map(e => (e.custom_hostname || '').toLowerCase()).filter(Boolean))].sort();
                setExistingDomains(domains);
            }
        } catch (err) {
            console.error('Error fetching existing domains:', err);
        }
    };

    const fetchEvent = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('accessToken');
            // Add cache-busting parameter to ensure fresh data
            const response = await fetch(`${API_URL}/events/${id}?_t=${Date.now()}`, {
                headers: { 'Authorization': `Bearer ${token}` },
                cache: 'no-cache'
            });
            
            if (response.ok) {
                const data = await response.json();
                setEvent(data);
                setFormData({
                    name: data.name || '',
                    description: data.description || '',
                    event_type: data.event_type || 'appointment',
                    oem_id: data.oem_id || '',
                    group_id: data.group_id || '',
                    dealer_location_ids: data.dealer_location_ids || [],
                    appointment_duration_minutes: data.appointment_duration_minutes || 30,
                    gap_duration_minutes: data.gap_duration_minutes || 15,
                    is_active: data.is_active !== undefined ? data.is_active : true,
                    is_public: data.is_public !== undefined ? data.is_public : true,
                    start_date: data.start_date ? data.start_date.slice(0, 10) : '',
                    end_date: data.end_date ? data.end_date.slice(0, 10) : '',
                    custom_hostname: data.custom_hostname || '',
                    short_code: data.short_code || '',
                    slug: data.slug || '',
                    banner_text: data.banner_text || '',
                    banner_image_url: data.banner_image_url || '',
                    banner_background_color: data.banner_background_color || '#ffffff',
                    banner_text_color: data.banner_text_color || '#000000',
                    banner_font_size: data.banner_font_size || 24,
                    banner_is_active: data.banner_is_active !== undefined ? data.banner_is_active : false,
                    address: data.address || '',
                    contact_email: data.contact_email || '',
                    contact_phone: data.contact_phone || ''
                });
                setSalesTeam(data.sales_team || []);
                setVehicleModels(data.vehicle_models || []);
                setAccessories(data.accessories || []);
            } else {
                setError('Failed to load event');
            }
        } catch (error) {
            console.error('Error fetching event:', error);
            setError('Failed to load event');
        } finally {
            setLoading(false);
        }
    };


    const handleBannerUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !id) return;
        const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowed.includes(file.type)) {
            alert('Invalid file type. Allowed: JPEG, PNG, GIF, WebP');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            alert('File too large. Maximum size: 5MB');
            return;
        }
        try {
            setBannerUploading(true);
            const token = localStorage.getItem('accessToken');
            const fd = new FormData();
            fd.append('banner', file);
            const response = await fetch(`${API_URL}/events/${id}/banner-upload`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: fd
            });
            const data = await response.json();
            if (response.ok) {
                setFormData(prev => ({ ...prev, banner_image_url: data.banner_image_url }));
                setEvent(prev => prev ? { ...prev, banner_image_url: data.banner_image_url } : null);
            } else {
                alert(data.error || data.message || 'Upload failed');
            }
        } catch (err) {
            alert(err.message || 'Upload failed');
        } finally {
            setBannerUploading(false);
            e.target.value = '';
        }
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_URL}/events/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    ...formData,
                    oem_id: formData.oem_id || null,
                    group_id: formData.group_id || null,
                    dealer_location_ids: formData.dealer_location_ids.length > 0 ? formData.dealer_location_ids : null,
                    custom_hostname: formData.custom_hostname || null,
                    start_date: formData.start_date || null,
                    end_date: formData.end_date || null,
                    banner_text: formData.banner_text || null,
                    banner_image_url: formData.banner_image_url || null,
                    banner_background_color: formData.banner_background_color || null,
                    banner_text_color: formData.banner_text_color || null,
                    banner_font_size: formData.banner_font_size || 24,
                    banner_is_active: formData.banner_is_active
                })
            });

            if (response.ok) {
                // Get updated event data from response to immediately update state
                const updatedEvent = await response.json();
                setEvent(updatedEvent);
                // Also update formData with any server-generated fields (like slug or short_code)
                if (updatedEvent.slug) {
                    setFormData(prev => ({ ...prev, slug: updatedEvent.slug }));
                }
                if (updatedEvent.short_code) {
                    setFormData(prev => ({ ...prev, short_code: updatedEvent.short_code }));
                }
                // Refresh full event data to ensure everything is in sync
                await fetchEvent();
                alert('Event saved successfully!');
            } else {
                const errorData = await response.json();
                setError(errorData.error || 'Failed to save event');
            }
        } catch (error) {
            console.error('Error saving event:', error);
            setError('Failed to save event');
        } finally {
            setSaving(false);
        }
    };

    const handleAddSalesTeam = async (userData) => {
        try {
            const token = localStorage.getItem('accessToken');
            const url = editingSalesTeam
                ? `${API_URL}/events/${id}/sales-team/${editingSalesTeam.id}`
                : `${API_URL}/events/${id}/sales-team`;
            const method = editingSalesTeam ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(userData)
            });

            if (response.ok) {
                await fetchEvent();
                setShowSalesTeamModal(false);
                setEditingSalesTeam(null);
                setError(''); // Clear any previous errors
            } else {
                const errorData = await response.json();
                const errorMessage = errorData.error || 'Failed to save sales team member';
                setError(errorMessage);
                // If it's a conflict, the member already exists - refresh the list
                if (response.status === 409) {
                    await fetchEvent();
                }
            }
        } catch (error) {
            console.error('Error saving sales team member:', error);
            setError('Failed to save sales team member');
        }
    };

    const handleDeleteSalesTeam = async (memberId) => {
        if (!window.confirm('Remove this sales team member?')) return;

        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_URL}/events/${id}/sales-team/${memberId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                await fetchEvent();
            } else {
                setError('Failed to remove sales team member');
            }
        } catch (error) {
            console.error('Error removing sales team member:', error);
            setError('Failed to remove sales team member');
        }
    };

    const handleAddVehicle = async (vehicleData) => {
        try {
            const token = localStorage.getItem('accessToken');
            const url = editingVehicle
                ? `${API_URL}/events/${id}/vehicle-models/${editingVehicle.id}`
                : `${API_URL}/events/${id}/vehicle-models`;
            const method = editingVehicle ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(vehicleData)
            });

            if (response.ok) {
                await fetchEvent();
                setShowVehicleModal(false);
                setEditingVehicle(null);
            } else {
                const errorData = await response.json();
                setError(errorData.error || 'Failed to save vehicle model');
            }
        } catch (error) {
            console.error('Error saving vehicle model:', error);
            setError('Failed to save vehicle model');
        }
    };

    const handleDeleteVehicle = async (vehicleId) => {
        if (!window.confirm('Delete this vehicle model?')) return;

        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_URL}/events/${id}/vehicle-models/${vehicleId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                await fetchEvent();
            } else {
                setError('Failed to delete vehicle model');
            }
        } catch (error) {
            console.error('Error deleting vehicle model:', error);
            setError('Failed to delete vehicle model');
        }
    };

    const handleAddAccessory = async (accessoryData) => {
        try {
            const token = localStorage.getItem('accessToken');
            const url = editingAccessory
                ? `${API_URL}/events/${id}/accessories/${editingAccessory.id}`
                : `${API_URL}/events/${id}/accessories`;
            const method = editingAccessory ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(accessoryData)
            });

            if (response.ok) {
                await fetchEvent();
                setShowAccessoryModal(false);
                setEditingAccessory(null);
            } else {
                const errorData = await response.json();
                setError(errorData.error || 'Failed to save accessory');
            }
        } catch (error) {
            console.error('Error saving accessory:', error);
            setError('Failed to save accessory');
        }
    };

    const handleDeleteAccessory = async (accessoryId) => {
        if (!window.confirm('Delete this accessory?')) return;

        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_URL}/events/${id}/accessories/${accessoryId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                await fetchEvent();
            } else {
                setError('Failed to delete accessory');
            }
        } catch (error) {
            console.error('Error deleting accessory:', error);
            setError('Failed to delete accessory');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-gray-500">Loading event...</div>
            </div>
        );
    }

    if (!event) {
        return (
            <div className="text-center py-12">
                <p className="text-gray-500">Event not found</p>
                <button onClick={() => navigate('/events')} className="mt-4 text-blue-600">
                    Back to Events
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/events')}
                        className="p-2 hover:bg-gray-100 rounded"
                    >
                        <ArrowLeft size={20} />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Configure Event</h1>
                        <p className="text-gray-600">{event.name}</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate(`/events/${id}/reports`)}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                        <BarChart3 size={20} />
                        View Reports
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                        <Save size={20} />
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                    {error}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Basic Information */}
                <div className="bg-white rounded-lg shadow p-6">
                    <h2 className="text-lg font-semibold mb-4">Basic Information</h2>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Event Name *
                            </label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Description
                            </label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                rows="3"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Event Type
                            </label>
                            <select
                                value={formData.event_type}
                                onChange={(e) => setFormData({ ...formData, event_type: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            >
                                <option value="appointment">Appointment</option>
                                <option value="test_drive">Test Drive</option>
                                <option value="service">Service</option>
                                <option value="consultation">Consultation</option>
                                <option value="viewing">Vehicle Viewing</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Ownership */}
                {!isClient && (
                <div className="bg-white rounded-lg shadow p-6">
                    <h2 className="text-lg font-semibold mb-4">Ownership</h2>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                OEM
                            </label>
                            <select
                                value={formData.oem_id}
                                onChange={(e) => setFormData({ ...formData, oem_id: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            >
                                <option value="">None (Independent)</option>
                                {oems.map(oem => (
                                    <option key={oem.id} value={oem.id}>{oem.name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Group
                            </label>
                            <div className="relative">
                                <select
                                    value={formData.group_id}
                                    onChange={(e) => {
                                        const newGroupId = e.target.value;
                                        const validLocationIds = newGroupId
                                            ? formData.dealer_location_ids.filter(locId => {
                                                const loc = dealerLocations.find(l => l.id === locId);
                                                return loc && loc.group_id === newGroupId;
                                            })
                                            : [];
                                        setFormData({ 
                                            ...formData, 
                                            group_id: newGroupId,
                                            dealer_location_ids: validLocationIds
                                        });
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                >
                                    <option value="">None</option>
                                    {groups.map(group => {
                                        const isSynced = group.is_voicebox_synced === true;
                                        return (
                                            <option key={group.id} value={group.id}>
                                                {group.name}{!isSynced && ' (Not Synced)'}
                                            </option>
                                        );
                                    })}
                                </select>
                                {formData.group_id && (() => {
                                    const selectedGroup = groups.find(g => g.id === formData.group_id);
                                    const isSynced = selectedGroup?.is_voicebox_synced === true;
                                    return isSynced ? (
                                        <div className="absolute right-10 top-1/2 transform -translate-y-1/2 pointer-events-none">
                                            <CheckCircle2 size={16} className="text-green-600" />
                                        </div>
                                    ) : null;
                                })()}
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Dealership Locations
                                {formData.group_id && (
                                    <span className="text-xs text-gray-500 ml-2">
                                        (filtered by selected group)
                                    </span>
                                )}
                            </label>
                            {!formData.group_id ? (
                                <div className="w-full px-3 py-8 border border-gray-300 rounded-md bg-gray-50 text-center text-gray-500">
                                    <p className="text-sm">Select a group first to see available locations</p>
                                </div>
                            ) : filteredDealerLocations.length === 0 ? (
                                <div className="w-full px-3 py-8 border border-gray-300 rounded-md bg-gray-50 text-center text-gray-500">
                                    <p className="text-sm">No location configured</p>
                                    <p className="text-xs mt-1">This group does not have any locations in the database</p>
                                </div>
                            ) : (
                                <select
                                    multiple
                                    value={formData.dealer_location_ids}
                                    onChange={(e) => {
                                        const selected = Array.from(e.target.selectedOptions, option => option.value);
                                        setFormData({ ...formData, dealer_location_ids: selected });
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md h-32"
                                >
                                    {filteredDealerLocations.map(location => (
                                        <option key={location.id} value={location.id}>
                                            {location.name} {location.group_name ? `(${location.group_name})` : ''}
                                        </option>
                                    ))}
                                </select>
                            )}
                            {formData.group_id && filteredDealerLocations.length > 0 && (
                                <p className="text-xs text-gray-500 mt-1">
                                    Hold Ctrl/Cmd to select multiple locations from this group
                                </p>
                            )}
                        </div>
                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <label className="block text-sm font-medium text-gray-700">
                                    Custom Hostname / Domain
                                    <span className="text-xs text-gray-500 ml-2">(Optional)</span>
                                </label>
                                <button
                                    type="button"
                                    onClick={() => navigate('/events/domains')}
                                    className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                                >
                                    <Globe className="w-3 h-3" />
                                    Manage All Domains
                                </button>
                            </div>
                            {existingDomains.length > 0 && (
                                <div className="mb-2">
                                    <label className="block text-xs text-gray-500 mb-1">Or select existing portal domain</label>
                                    <select
                                        value=""
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            if (val) setFormData({ ...formData, custom_hostname: val });
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                    >
                                        <option value="">-- Select existing domain --</option>
                                        {existingDomains.map((d) => (
                                            <option key={d} value={d}>{d}</option>
                                        ))}
                                    </select>
                                    <p className="text-xs text-gray-500 mt-1">Selecting an existing domain adds this event to the portal. No new SSL setup needed.</p>
                                </div>
                            )}
                            <input
                                type="text"
                                value={formData.custom_hostname}
                                onChange={(e) => setFormData({ ...formData, custom_hostname: e.target.value.trim() })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                placeholder="e.g., booking.bmw-dealership.com"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Configure custom domains, DNS settings, and SSL certificates in <button type="button" onClick={() => navigate('/events/domains')} className="text-indigo-600 hover:underline font-medium">Domain Management</button>
                            </p>
                            
                            {formData.custom_hostname && (
                                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <p className="text-xs font-medium text-blue-900 mb-1">Custom domain configured</p>
                                            <p className="text-xs text-blue-700">
                                                Domain: <code className="bg-white px-1 rounded">{formData.custom_hostname}</code>
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => navigate('/events/domains')}
                                            className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1 px-2 py-1 bg-white rounded border border-indigo-200 whitespace-nowrap"
                                        >
                                            <Globe className="w-3 h-3" />
                                            Manage Domain
                                        </button>
                                    </div>
                                    <p className="text-xs text-blue-600 mt-2">
                                        For DNS configuration, SSL certificates, and domain status, visit <button type="button" onClick={() => navigate('/events/domains')} className="text-indigo-600 hover:underline font-medium">Domain Management</button>
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                )}

                {/* Timing */}
                <div className="bg-white rounded-lg shadow p-6">
                    <h2 className="text-lg font-semibold mb-4">Timing</h2>
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Event start date
                                </label>
                                <input
                                    type="date"
                                    value={formData.start_date}
                                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                                <p className="text-xs text-gray-500 mt-1">Event becomes available on this date. Leave empty for no start limit.</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Event end date
                                </label>
                                <input
                                    type="date"
                                    value={formData.end_date}
                                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                                <p className="text-xs text-gray-500 mt-1">Event stops being public after this date. Leave empty for no end limit.</p>
                            </div>
                        </div>
                        <div className="pt-2 border-t border-gray-200">
                            <p className="text-sm text-gray-600 mb-3">Within the start/end date range, the event is available and public unless you override below.</p>
                            <div className="flex items-center gap-4">
                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={formData.is_active}
                                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                        className="mr-2"
                                    />
                                    <span className="text-sm text-gray-700">Active</span>
                                </label>
                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={formData.is_public}
                                        onChange={(e) => setFormData({ ...formData, is_public: e.target.checked })}
                                        className="mr-2"
                                    />
                                    <span className="text-sm text-gray-700">Public</span>
                                </label>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">Uncheck to hide or disable the event even within the date span.</p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Appointment Duration (minutes)
                            </label>
                            <input
                                type="number"
                                value={formData.appointment_duration_minutes}
                                onChange={(e) => setFormData({ ...formData, appointment_duration_minutes: parseInt(e.target.value) })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                min="15"
                                step="15"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Gap Duration (minutes)
                            </label>
                            <input
                                type="number"
                                value={formData.gap_duration_minutes}
                                onChange={(e) => setFormData({ ...formData, gap_duration_minutes: parseInt(e.target.value) })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                min="0"
                                step="5"
                            />
                        </div>
                    </div>
                </div>

                {/* Venue & Contact */}
                <div className="bg-white rounded-lg shadow p-6">
                    <h2 className="text-lg font-semibold mb-4">Venue & Contact</h2>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Venue Address
                            </label>
                            <textarea
                                value={formData.address}
                                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                rows="3"
                                placeholder="Full address of the event venue"
                            />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Contact Email
                                </label>
                                <input
                                    type="email"
                                    value={formData.contact_email}
                                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    placeholder="e.g. events@dealership.co.uk"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Contact Phone
                                </label>
                                <input
                                    type="tel"
                                    value={formData.contact_phone}
                                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    placeholder="e.g. +44 123 456 7890"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Sales Team */}
                <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center justify-between mb-2">
                        <h2 className="text-lg font-semibold">Sales Team</h2>
                        <button
                            onClick={() => {
                                setEditingSalesTeam(null);
                                setShowSalesTeamModal(true);
                            }}
                            className="flex items-center gap-2 px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                        >
                            <Plus size={16} />
                            Add Member
                        </button>
                    </div>
                    <p className="text-xs text-gray-500 mb-4">Member availability only applies during the event&apos;s active dates (see Timing).</p>
                    <div className="space-y-2">
                        {salesTeam.length === 0 ? (
                            <p className="text-sm text-gray-500">No sales team members added</p>
                        ) : (
                            salesTeam.map((member) => (
                                <div key={member.id} className="p-4 border rounded-lg">
                                    <div className="flex items-center justify-between mb-2">
                                        <div>
                                            <div className="font-medium">
                                                {member.display_name || member.display_name_final || member.name || `${member.first_name || ''} ${member.last_name || ''}`.trim() || 'Unnamed'}
                                            </div>
                                            <div className="text-sm text-gray-500">
                                                {member.email_final || member.email || member.user_email}
                                            </div>
                                            {member.phone && (
                                                <div className="text-sm text-gray-500">{member.phone}</div>
                                            )}
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => {
                                                    setEditingSalesTeam(member);
                                                    setShowSalesTeamModal(true);
                                                }}
                                                className="text-blue-600 hover:text-blue-700"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => handleDeleteSalesTeam(member.id)}
                                                className="text-red-600 hover:text-red-700"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                    {/* Availability Summary */}
                                    {(member.availability_start_time || member.availability_days_of_week) && (
                                        <div className="mt-2 pt-2 border-t text-xs text-gray-600">
                                            <div className="flex gap-4">
                                                {member.availability_start_time && member.availability_end_time && (
                                                    <span>
                                                        {member.availability_start_time} - {member.availability_end_time}
                                                    </span>
                                                )}
                                                {member.availability_days_of_week && member.availability_days_of_week.length > 0 && (
                                                    <span>
                                                        Days: {member.availability_days_of_week.map(d => ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'][d-1]).join(', ')}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Vehicle Models */}
                <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold">Vehicle Models</h2>
                        <button
                            onClick={() => {
                                setEditingVehicle(null);
                                setShowVehicleModal(true);
                            }}
                            className="flex items-center gap-2 px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                        >
                            <Plus size={16} />
                            Add Vehicle
                        </button>
                    </div>
                    <div className="space-y-2">
                        {vehicleModels.length === 0 ? (
                            <p className="text-sm text-gray-500">No vehicle models added</p>
                        ) : (
                            vehicleModels.map((vehicle) => (
                                <div key={vehicle.id} className="flex items-center justify-between p-3 border rounded">
                                    <div>
                                        <div className="font-medium">{vehicle.model_name}</div>
                                        {vehicle.model_year && (
                                            <div className="text-sm text-gray-500">{vehicle.model_year}</div>
                                        )}
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => {
                                                setEditingVehicle(vehicle);
                                                setShowVehicleModal(true);
                                            }}
                                            className="text-blue-600 hover:text-blue-700"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => handleDeleteVehicle(vehicle.id)}
                                            className="text-red-600 hover:text-red-700"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Accessories */}
                <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold">Accessories</h2>
                        <button
                            onClick={() => {
                                setEditingAccessory(null);
                                setShowAccessoryModal(true);
                            }}
                            className="flex items-center gap-2 px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                        >
                            <Plus size={16} />
                            Add Accessory
                        </button>
                    </div>
                    <div className="space-y-2">
                        {accessories.length === 0 ? (
                            <p className="text-sm text-gray-500">No accessories added</p>
                        ) : (
                            accessories.map((accessory) => (
                                <div key={accessory.id} className="flex items-center justify-between p-3 border rounded">
                                    <div>
                                        <div className="font-medium">{accessory.name}</div>
                                        {accessory.price && (
                                            <div className="text-sm text-gray-500">${accessory.price}</div>
                                        )}
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => {
                                                setEditingAccessory(accessory);
                                                setShowAccessoryModal(true);
                                            }}
                                            className="text-blue-600 hover:text-blue-700"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => handleDeleteAccessory(accessory.id)}
                                            className="text-red-600 hover:text-red-700"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Banner Configuration */}
                <div className="bg-white rounded-lg shadow p-6 col-span-full">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <Image size={20} className="text-gray-600" />
                            <h2 className="text-lg font-semibold">Event Banner</h2>
                        </div>
                        <label className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={formData.banner_is_active}
                                onChange={(e) => setFormData({ ...formData, banner_is_active: e.target.checked })}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">Enable Banner</span>
                        </label>
                    </div>

                    <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-sm font-medium text-blue-900 mb-2">Banner Size Requirements</p>
                        <ul className="text-sm text-blue-800 space-y-1">
                            <li>• Recommended width: <strong>1200px</strong> (will scale responsively)</li>
                            <li>• Recommended height: <strong>200-400px</strong> (optimal: 300px)</li>
                            <li>• Maximum height: <strong>500px</strong> (to maintain page balance)</li>
                            <li>• Banner appears above the event booking details on the public page</li>
                        </ul>
                    </div>

                    {formData.banner_is_active && (
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Banner Text
                                </label>
                                <textarea
                                    value={formData.banner_text}
                                    onChange={(e) => setFormData({ ...formData, banner_text: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                    rows="3"
                                    placeholder="Enter banner text (e.g., Special offer, event announcement, etc.)"
                                />
                                <p className="text-xs text-gray-500 mt-1">This text will be displayed prominently in the banner</p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Banner Image
                                </label>
                                <div className="flex flex-wrap items-center gap-2 mb-2">
                                    <label className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer disabled:opacity-50">
                                        <Upload size={18} />
                                        {bannerUploading ? 'Uploading...' : 'Upload image'}
                                        <input
                                            type="file"
                                            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                                            onChange={handleBannerUpload}
                                            disabled={bannerUploading}
                                            className="hidden"
                                        />
                                    </label>
                                    <span className="text-xs text-gray-500">JPEG, PNG, GIF, WebP (max 5MB)</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="url"
                                        value={formData.banner_image_url}
                                        onChange={(e) => setFormData({ ...formData, banner_image_url: e.target.value })}
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                        placeholder="Or paste image URL (https://...)"
                                    />
                                    {formData.banner_image_url && (
                                        <button
                                            onClick={() => setFormData({ ...formData, banner_image_url: '' })}
                                            className="p-2 text-gray-500 hover:text-red-600"
                                            title="Clear image"
                                        >
                                            <X size={18} />
                                        </button>
                                    )}
                                </div>
                                <p className="text-xs text-gray-500 mt-1">Upload an image or paste a URL. Text will overlay if both are provided.</p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Background Color
                                    </label>
                                    <div className="flex gap-2">
                                        <input
                                            type="color"
                                            value={formData.banner_background_color}
                                            onChange={(e) => setFormData({ ...formData, banner_background_color: e.target.value })}
                                            className="h-10 w-20 border border-gray-300 rounded cursor-pointer"
                                        />
                                        <input
                                            type="text"
                                            value={formData.banner_background_color}
                                            onChange={(e) => setFormData({ ...formData, banner_background_color: e.target.value })}
                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                            placeholder="#ffffff"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Text Color
                                    </label>
                                    <div className="flex gap-2">
                                        <input
                                            type="color"
                                            value={formData.banner_text_color}
                                            onChange={(e) => setFormData({ ...formData, banner_text_color: e.target.value })}
                                            className="h-10 w-20 border border-gray-300 rounded cursor-pointer"
                                        />
                                        <input
                                            type="text"
                                            value={formData.banner_text_color}
                                            onChange={(e) => setFormData({ ...formData, banner_text_color: e.target.value })}
                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                            placeholder="#000000"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Font Size ({formData.banner_font_size}px)
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.banner_font_size}
                                        onChange={(e) => setFormData({ ...formData, banner_font_size: Math.max(12, Math.min(72, parseInt(e.target.value) || 12)) })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 mb-2"
                                        min="12"
                                        max="72"
                                    />
                                    <input
                                        type="range"
                                        min="12"
                                        max="72"
                                        value={formData.banner_font_size}
                                        onChange={(e) => setFormData({ ...formData, banner_font_size: parseInt(e.target.value) })}
                                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                                    />
                                </div>
                            </div>

                            {/* Banner Preview */}
                            {(formData.banner_text || formData.banner_image_url) && (
                                <div className="mt-6">
                                    <div className="flex items-center gap-2 mb-3">
                                        <Eye size={18} className="text-gray-600" />
                                        <h3 className="text-sm font-medium text-gray-700">Banner Preview</h3>
                                        <span className="text-xs text-gray-500">(1200px × 300px recommended)</span>
                                    </div>
                                    <div 
                                        className="w-full border-2 border-dashed border-gray-300 rounded-lg overflow-hidden"
                                        style={{ 
                                            backgroundColor: formData.banner_background_color,
                                            minHeight: '200px',
                                            maxHeight: '500px',
                                            position: 'relative'
                                        }}
                                    >
                                        {formData.banner_image_url ? (
                                            <div className="relative w-full h-full" style={{ minHeight: '200px', maxHeight: '500px' }}>
                                                <img 
                                                    src={formData.banner_image_url.startsWith('/uploads/') 
                                                        ? `${API_URL.replace(/\/api\/?$/, '')}${formData.banner_image_url}`
                                                        : formData.banner_image_url
                                                    } 
                                                    alt="Banner preview"
                                                    className="w-full h-full object-cover"
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                    }}
                                                />
                                                {formData.banner_text && (
                                                    <div 
                                                        className="absolute inset-0 flex items-center justify-center p-6"
                                                        style={{ color: formData.banner_text_color }}
                                                    >
                                                        <p className="text-center font-semibold" style={{ fontSize: `${formData.banner_font_size}px`, textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                                                            {formData.banner_text}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        ) : formData.banner_text ? (
                                            <div 
                                                className="flex items-center justify-center p-6 h-full min-h-[200px]"
                                                style={{ color: formData.banner_text_color }}
                                            >
                                                <p className="text-center font-semibold" style={{ fontSize: `${formData.banner_font_size}px` }}>
                                                    {formData.banner_text}
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="flex items-center justify-center p-6 h-full min-h-[200px] text-gray-400">
                                                <p className="text-center">Banner preview will appear here</p>
                                            </div>
                                        )}
                                    </div>
                                    <p className="text-xs text-gray-500 mt-2">
                                        This banner will appear above the event booking form on the public event page.
                                    </p>
                                </div>
                            )}

                            {!formData.banner_text && !formData.banner_image_url && (
                                <div className="p-4 bg-gray-50 rounded border border-gray-200 text-center text-sm text-gray-500">
                                    Configure banner text or image to see preview
                                </div>
                            )}
                        </div>
                    )}

                    {!formData.banner_is_active && (
                        <div className="p-4 bg-gray-50 rounded border border-gray-200 text-center text-sm text-gray-500">
                            Banner is disabled. Enable it above to configure.
                        </div>
                    )}
                </div>
            </div>

            {/* Sales Team Modal */}
            {showSalesTeamModal && (
                <SalesTeamModal
                    isOpen={showSalesTeamModal}
                    onClose={() => {
                        setShowSalesTeamModal(false);
                        setEditingSalesTeam(null);
                    }}
                    users={users}
                    editing={editingSalesTeam}
                    onSave={handleAddSalesTeam}
                    eventStartDate={formData.start_date}
                    eventEndDate={formData.end_date}
                />
            )}

            {/* Vehicle Modal */}
            {showVehicleModal && (
                <VehicleModal
                    isOpen={showVehicleModal}
                    onClose={() => {
                        setShowVehicleModal(false);
                        setEditingVehicle(null);
                    }}
                    brands={brands}
                    editing={editingVehicle}
                    onSave={handleAddVehicle}
                />
            )}

            {/* Accessory Modal */}
            {showAccessoryModal && (
                <AccessoryModal
                    isOpen={showAccessoryModal}
                    onClose={() => {
                        setShowAccessoryModal(false);
                        setEditingAccessory(null);
                    }}
                    editing={editingAccessory}
                    onSave={handleAddAccessory}
                />
            )}
        </div>
    );
}

// Sales Team Modal Component
function SalesTeamModal({ isOpen, onClose, users, editing, onSave, eventStartDate, eventEndDate }) {
    const [useUser, setUseUser] = useState(editing?.user_id ? true : false);
    const [formData, setFormData] = useState({
        user_id: editing?.user_id || '',
        name: editing?.name || '',
        email: editing?.email || editing?.email_final || '',
        phone: editing?.phone || '',
        display_name: editing?.display_name || '',
        bio: editing?.bio || '',
        availability_start_time: editing?.availability_start_time || '',
        availability_end_time: editing?.availability_end_time || '',
        availability_days_of_week: editing?.availability_days_of_week || [1, 2, 3, 4, 5],
        appointment_duration_minutes: editing?.appointment_duration_minutes || '',
        gap_duration_minutes: editing?.gap_duration_minutes || ''
    });

    // Sync form when opening for edit (or reset when opening for add)
    useEffect(() => {
        if (editing) {
            setUseUser(!!editing.user_id);
            setFormData({
                user_id: editing.user_id || '',
                name: editing.name || '',
                email: editing.email || editing.email_final || '',
                phone: editing.phone || '',
                display_name: editing.display_name || '',
                bio: editing.bio || '',
                availability_start_time: editing.availability_start_time || '',
                availability_end_time: editing.availability_end_time || '',
                availability_days_of_week: Array.isArray(editing.availability_days_of_week)
                    ? editing.availability_days_of_week
                    : (editing.availability_days_of_week ? [].concat(editing.availability_days_of_week) : [1, 2, 3, 4, 5]),
                appointment_duration_minutes: editing.appointment_duration_minutes ?? '',
                gap_duration_minutes: editing.gap_duration_minutes ?? ''
            });
        } else {
            setUseUser(false);
            setFormData({
                user_id: '',
                name: '',
                email: '',
                phone: '',
                display_name: '',
                bio: '',
                availability_start_time: '',
                availability_end_time: '',
                availability_days_of_week: [1, 2, 3, 4, 5],
                appointment_duration_minutes: '',
                gap_duration_minutes: ''
            });
        }
    }, [editing]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!useUser && (!formData.name || !formData.email)) {
            alert('Name and Email are required when not using a user account');
            return;
        }
        onSave(formData);
    };

    const toggleDay = (day) => {
        const days = formData.availability_days_of_week || [];
        if (days.includes(day)) {
            setFormData({ ...formData, availability_days_of_week: days.filter(d => d !== day) });
        } else {
            setFormData({ ...formData, availability_days_of_week: [...days, day] });
        }
    };

    if (!isOpen) return null;

    const daysOfWeek = [
        { value: 1, label: 'Monday' },
        { value: 2, label: 'Tuesday' },
        { value: 3, label: 'Wednesday' },
        { value: 4, label: 'Thursday' },
        { value: 5, label: 'Friday' },
        { value: 6, label: 'Saturday' },
        { value: 7, label: 'Sunday' }
    ];

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={editing ? 'Edit Sales Team Member' : 'Add Sales Team Member'}>
            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className="flex items-center mb-2">
                        <input
                            type="checkbox"
                            checked={useUser}
                            onChange={(e) => setUseUser(e.target.checked)}
                            className="mr-2"
                            disabled={!!editing?.user_id}
                        />
                        <span className="text-sm font-medium text-gray-700">Link to existing user account</span>
                    </label>
                    {useUser ? (
                        <select
                            value={formData.user_id}
                            onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            required={useUser}
                            disabled={!!editing?.user_id}
                        >
                            <option value="">Select a user</option>
                            {users.map(user => (
                                <option key={user.id} value={user.id}>
                                    {user.first_name} {user.last_name} ({user.email})
                                </option>
                            ))}
                        </select>
                    ) : (
                        <div className="space-y-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Name *
                                </label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    required={!useUser}
                                    placeholder="Sales rep name"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Email *
                                </label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    required={!useUser}
                                    placeholder="sales.rep@dealership.com"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Phone
                                </label>
                                <input
                                    type="tel"
                                    value={formData.phone}
                                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    placeholder="+44 123 456 7890"
                                />
                            </div>
                        </div>
                    )}
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Display Name
                    </label>
                    <input
                        type="text"
                        value={formData.display_name}
                        onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        placeholder="Override name for this event"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Bio
                    </label>
                    <textarea
                        value={formData.bio}
                        onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        rows="3"
                        placeholder="Brief description or title"
                    />
                </div>

                {/* Availability Configuration */}
                <div className="border-t pt-4">
                    <div className="flex items-center gap-2 mb-3">
                        <Calendar size={16} className="text-gray-600" />
                        <h3 className="text-sm font-semibold text-gray-900">Availability Settings</h3>
                    </div>
                    <p className="text-xs text-gray-500 mb-2">Configure when this sales person is available. Leave empty to use event defaults.</p>
                    {(eventStartDate || eventEndDate) ? (
                        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mb-4">
                            Availability only applies during the event&apos;s active dates{eventStartDate && eventEndDate ? ` (${eventStartDate} to ${eventEndDate})` : eventStartDate ? ` (from ${eventStartDate})` : eventEndDate ? ` (until ${eventEndDate})` : ''}. Bookable slots are limited to this range.
                        </p>
                    ) : (
                        <p className="text-xs text-gray-500 mb-4">No event start/end dates set — availability applies to all dates until you add them in Timing.</p>
                    )}
                    
                    <div className="space-y-4">
                        {/* Days of Week Selection */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Available Days *
                            </label>
                            <div className="flex flex-wrap gap-2">
                                {daysOfWeek.map(day => (
                                    <button
                                        key={day.value}
                                        type="button"
                                        onClick={() => toggleDay(day.value)}
                                        className={`px-3 py-2 rounded text-sm font-medium transition ${
                                            formData.availability_days_of_week?.includes(day.value)
                                                ? 'bg-blue-600 text-white shadow-sm'
                                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                        }`}
                                    >
                                        {day.label}
                                    </button>
                                ))}
                            </div>
                            {(!formData.availability_days_of_week || formData.availability_days_of_week.length === 0) && (
                                <p className="text-xs text-red-500 mt-1">At least one day must be selected</p>
                            )}
                        </div>

                        {/* Time Range */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Available From
                                </label>
                                <input
                                    type="time"
                                    value={formData.availability_start_time || ''}
                                    onChange={(e) => setFormData({ ...formData, availability_start_time: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    placeholder="09:00"
                                />
                                <p className="text-xs text-gray-500 mt-1">Start of availability window</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Available Until
                                </label>
                                <input
                                    type="time"
                                    value={formData.availability_end_time || ''}
                                    onChange={(e) => setFormData({ ...formData, availability_end_time: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    placeholder="17:00"
                                />
                                <p className="text-xs text-gray-500 mt-1">End of availability window</p>
                            </div>
                        </div>

                        {/* Duration Settings */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Appointment Duration (minutes)
                                </label>
                                <input
                                    type="number"
                                    value={formData.appointment_duration_minutes || ''}
                                    onChange={(e) => setFormData({ ...formData, appointment_duration_minutes: e.target.value ? parseInt(e.target.value) : '' })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    min="15"
                                    step="15"
                                    placeholder="30"
                                />
                                <p className="text-xs text-gray-500 mt-1">Length of each appointment</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Gap Between Appointments (minutes)
                                </label>
                                <input
                                    type="number"
                                    value={formData.gap_duration_minutes || ''}
                                    onChange={(e) => setFormData({ ...formData, gap_duration_minutes: e.target.value ? parseInt(e.target.value) : '' })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                    min="0"
                                    step="5"
                                    placeholder="15"
                                />
                                <p className="text-xs text-gray-500 mt-1">Buffer time between appointments</p>
                            </div>
                        </div>

                        {/* Time Slot Preview */}
                        {formData.availability_start_time && formData.availability_end_time && formData.availability_days_of_week && formData.availability_days_of_week.length > 0 && (
                            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
                                <p className="text-xs font-medium text-blue-900 mb-2">Availability Summary:</p>
                                <p className="text-xs text-blue-700">
                                    Available {formData.availability_days_of_week.map(d => daysOfWeek.find(day => day.value === d)?.label.substring(0, 3)).join(', ')} 
                                    {' '}from {formData.availability_start_time} to {formData.availability_end_time}
                                    {formData.appointment_duration_minutes && ` (${formData.appointment_duration_minutes} min appointments`}
                                    {formData.gap_duration_minutes && `, ${formData.gap_duration_minutes} min gaps`}
                                    {formData.appointment_duration_minutes && ')'}
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                    <button type="button" onClick={onClose} className="px-4 py-2 border rounded">
                        Cancel
                    </button>
                    <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded">
                        {editing ? 'Update' : 'Add'}
                    </button>
                </div>
            </form>
        </Modal>
    );
}

// Vehicle Modal Component
function VehicleModal({ isOpen, onClose, brands, editing, onSave }) {
    const [formData, setFormData] = useState({
        brand_id: editing?.brand_id || '',
        model_name: editing?.model_name || '',
        model_year: editing?.model_year || '',
        description: editing?.description || '',
        image_url: editing?.image_url || ''
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave({
            ...formData,
            model_year: formData.model_year ? parseInt(formData.model_year) : null
        });
    };

    if (!isOpen) return null;

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={editing ? 'Edit Vehicle Model' : 'Add Vehicle Model'}>
            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Brand
                    </label>
                    <select
                        value={formData.brand_id}
                        onChange={(e) => setFormData({ ...formData, brand_id: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                        <option value="">Select a brand</option>
                        {brands.map(brand => (
                            <option key={brand.id} value={brand.id}>{brand.name}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Model Name *
                    </label>
                    <input
                        type="text"
                        value={formData.model_name}
                        onChange={(e) => setFormData({ ...formData, model_name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        required
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Model Year
                    </label>
                    <input
                        type="number"
                        value={formData.model_year}
                        onChange={(e) => setFormData({ ...formData, model_year: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        min="1900"
                        max="2100"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                    </label>
                    <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        rows="3"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Image URL
                    </label>
                    <input
                        type="url"
                        value={formData.image_url}
                        onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                </div>
                <div className="flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="px-4 py-2 border rounded">
                        Cancel
                    </button>
                    <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded">
                        {editing ? 'Update' : 'Add'}
                    </button>
                </div>
            </form>
        </Modal>
    );
}

// Accessory Modal Component
function AccessoryModal({ isOpen, onClose, editing, onSave }) {
    const [formData, setFormData] = useState({
        name: editing?.name || '',
        description: editing?.description || '',
        image_url: editing?.image_url || '',
        price: editing?.price || ''
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave({
            ...formData,
            price: formData.price ? parseFloat(formData.price) : null
        });
    };

    if (!isOpen) return null;

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={editing ? 'Edit Accessory' : 'Add Accessory'}>
            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Name *
                    </label>
                    <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        required
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                    </label>
                    <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        rows="3"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Price
                    </label>
                    <input
                        type="number"
                        step="0.01"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Image URL
                    </label>
                    <input
                        type="url"
                        value={formData.image_url}
                        onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                </div>
                <div className="flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="px-4 py-2 border rounded">
                        Cancel
                    </button>
                    <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded">
                        {editing ? 'Update' : 'Add'}
                    </button>
                </div>
            </form>
        </Modal>
    );
}

